import { useLenis } from "lenis/react";
import { MapPin, Phone, MessageCircle, Navigation, Clock } from "lucide-react";
import { ToothIcon } from "@/components/icons";
import { callClinic, whatsappClinic, openDirections } from "@/components/CTAButtons";
import { NAV_LINKS, TREATMENTS, ADDRESS_LINES, PHONE_DISPLAY, PHONE_NUMBER, WHATSAPP_NUMBER, TIMINGS } from "@/data/clinic";

export const Footer = () => {
  const lenis = useLenis();
  const go = (id) => {
    if (lenis) lenis.scrollTo(`#${id}`, { offset: -64 });
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer data-testid="site-footer" className="bg-[#063A2F] pb-28 pt-16 text-[#F2FBF7]/80 sm:pb-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[#14B8A6] text-[#063A2F]">
                <ToothIcon className="h-5 w-5" />
              </span>
              <span className="font-display text-xl font-semibold tracking-tight text-[#FDFCF8]">
                Smart Dental <span className="italic text-[#2DD4BF]">Clinic</span>
              </span>
            </div>
            <p className="mt-3 text-sm italic text-[#D4AF37]">Your Smile, Our Priority</p>
            <p className="mt-2 flex items-center gap-2 text-xs text-[#F2FBF7]/50">
              <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={1.5} />
              Bahadurpura, Hyderabad, Telangana
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                data-testid="footer-call-btn"
                onClick={callClinic}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#14B8A6] px-4 py-2 text-xs font-semibold text-[#063A2F] transition-colors duration-200 hover:bg-[#2DD4BF]"
              >
                <Phone className="h-3.5 w-3.5" strokeWidth={1.5} /> Call
              </button>
              <button
                data-testid="footer-whatsapp-btn"
                onClick={whatsappClinic}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#2DD4BF]/50 px-4 py-2 text-xs font-semibold text-[#5EEAD4] transition-colors duration-200 hover:bg-[#14B8A6] hover:text-[#063A2F]"
              >
                <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> WhatsApp
              </button>
              <button
                data-testid="footer-directions-btn"
                onClick={openDirections}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-[#F2FBF7]/80 transition-colors duration-200 hover:bg-white/10"
              >
                <Navigation className="h-3.5 w-3.5" strokeWidth={1.5} /> Directions
              </button>
            </div>
          </div>

          <nav aria-label="Quick links">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Quick Links</h3>
            <ul className="mt-4 space-y-2.5">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`footer-link-${l.id}`}
                    onClick={() => go(l.id)}
                    className="text-sm text-[#F2FBF7]/60 transition-colors duration-200 hover:text-[#FDFCF8]"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Treatments</h3>
            <ul className="mt-4 space-y-2.5">
              {TREATMENTS.slice(0, 6).map((t) => (
                <li key={t.icon}>
                  <button
                    onClick={() => go("treatments")}
                    className="text-sm text-[#F2FBF7]/60 transition-colors duration-200 hover:text-[#FDFCF8]"
                  >
                    {t.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#D4AF37]">
              <Clock className="h-3.5 w-3.5" strokeWidth={1.5} /> Clinic Timings
            </h3>
            <ul className="mt-4 space-y-3 text-xs leading-relaxed text-[#F2FBF7]/60" data-testid="footer-timings">
              {TIMINGS.map((t) => (
                <li key={t.day}>
                  <span className="block font-semibold text-[#F2FBF7]/85">{t.day}</span>
                  {t.closed ? (
                    <span className="text-[#D4AF37]">By Appointment Only</span>
                  ) : (
                    t.hours.map((h) => (
                      <span key={h} className="block">
                        {h}
                      </span>
                    ))
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="text-xs leading-relaxed text-[#F2FBF7]/55">
              <p className="font-semibold text-[#F2FBF7]/80">Contact</p>
              <p className="mt-1.5 max-w-md">{ADDRESS_LINES.join(" ")}</p>
              <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                <a href={`tel:${PHONE_NUMBER}`} className="inline-flex items-center gap-1.5 transition-colors duration-200 hover:text-[#FDFCF8]">
                  <Phone className="h-3 w-3 text-[#2DD4BF]" strokeWidth={1.5} /> {PHONE_DISPLAY}
                </a>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 transition-colors duration-200 hover:text-[#FDFCF8]"
                >
                  <MessageCircle className="h-3 w-3 text-[#2DD4BF]" strokeWidth={1.5} /> WhatsApp {PHONE_DISPLAY}
                </a>
              </p>
            </div>
            <p className="text-xs text-[#F2FBF7]/45">Walk-ins welcome · First-come, first-served</p>
          </div>
          <p className="mt-6 border-t border-white/10 pt-5 text-center text-xs text-[#F2FBF7]/45">
            © 2026 Smart Dental Clinic. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
